import React, { useState, useEffect } from 'react';
import { vitalsAPI, geoDeviceAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Zap, Globe, Monitor, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const RATING_COLOR = { good: '#1F7A5A', 'needs-improvement': '#9A6B1A', poor: '#B3261E' };
const COLORS = ['hsl(210, 55%, 40%)', 'hsl(160, 45%, 35%)', 'hsl(38, 55%, 45%)', 'hsl(4, 65%, 45%)', 'hsl(260, 35%, 45%)'];

function VitalCard({ label, value, unit, rating, threshold }) {
  const color = RATING_COLOR[rating] || '#5B616E';
  return (
    <Card className="bg-white border-[#E7E0D6]">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-[#5B616E] font-medium">{label}</span>
          <Badge style={{ background: `${color}15`, color }} className="text-[10px]">{rating}</Badge>
        </div>
        <p className="text-2xl font-bold">{value}{unit}</p>
        <p className="text-[10px] text-[#5B616E] mt-0.5">Good: {threshold}</p>
      </CardContent>
    </Card>
  );
}

export default function AdminWebVitals() {
  const [vitals, setVitals] = useState(null);
  const [geo, setGeo] = useState(null);
  const [optTime, setOptTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('7');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [v, g, o] = await Promise.all([
        vitalsAPI.get(parseInt(days)).catch(() => ({ data: null })),
        geoDeviceAPI.get(parseInt(days)).catch(() => ({ data: null })),
        geoDeviceAPI.optimalTime(30).catch(() => ({ data: null })),
      ]);
      setVitals(v.data);
      setGeo(g.data);
      setOptTime(o.data);
    } catch {}
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Zap className="w-6 h-6 text-[#B89B5E]" /> Performance & Analytics</h1>
          <p className="text-sm text-[#5B616E] mt-1">Core Web Vitals, geo distribution, and optimal publishing windows</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="14">14 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : (
        <>
          {/* Core Web Vitals */}
          <div>
            <h2 className="text-sm font-semibold text-[#5B616E] uppercase tracking-wide mb-3">Core Web Vitals (avg, {vitals?.sample_size || 0} samples)</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <VitalCard label="LCP" value={vitals?.averages?.lcp ?? '—'} unit="ms" rating={vitals?.ratings?.lcp || 'good'} threshold="≤2500ms" />
              <VitalCard label="FID" value={vitals?.averages?.fid ?? '—'} unit="ms" rating={vitals?.ratings?.fid || 'good'} threshold="≤100ms" />
              <VitalCard label="CLS" value={vitals?.averages?.cls ?? '—'} unit="" rating={vitals?.ratings?.cls || 'good'} threshold="≤0.1" />
              <VitalCard label="INP" value={vitals?.averages?.inp ?? '—'} unit="ms" rating={vitals?.ratings?.inp || 'good'} threshold="≤200ms" />
              <VitalCard label="TTFB" value={vitals?.averages?.ttfb ?? '—'} unit="ms" rating={vitals?.ratings?.ttfb || 'good'} threshold="≤800ms" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device breakdown */}
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Monitor className="w-4 h-4" /> Device Breakdown</CardTitle></CardHeader>
              <CardContent>
                {(geo?.by_device || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={geo.by_device.map(d => ({ name: d.device, value: d.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                        {(geo?.by_device || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-[#5B616E] text-center py-8">No data yet</p>}
                <div className="mt-2 space-y-1">
                  {(geo?.by_device || []).map((d, i) => (
                    <div key={d.device} className="flex justify-between text-xs">
                      <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{d.device}</span>
                      <span className="font-medium">{d.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top countries */}
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4" /> Top Countries</CardTitle></CardHeader>
              <CardContent>
                {(geo?.by_country || []).length > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {geo.by_country.slice(0, 10).map((c, i) => (
                      <div key={c.country} className="flex items-center gap-2">
                        <span className="text-xs w-5 text-[#5B616E] text-right">{i + 1}</span>
                        <span className="text-xs flex-1">{c.country}</span>
                        <div className="flex-1 bg-[#E7E0D6] rounded-full h-2">
                          <div className="bg-[#1E5A8A] h-2 rounded-full" style={{ width: `${Math.round((c.count / (geo.by_country[0]?.count || 1)) * 100)}%` }} />
                        </div>
                        <span className="text-xs font-medium w-12 text-right">{c.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#5B616E] text-center py-8">No data yet</p>}
              </CardContent>
            </Card>
          </div>

          {/* Optimal publish time */}
          <Card className="bg-white border-[#E7E0D6]">
            <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Optimal Publishing Time (last 30 days)</CardTitle></CardHeader>
            <CardContent>
              {optTime?.by_hour ? (
                <>
                  <p className="text-sm font-medium text-[#1F7A5A] mb-4">{optTime.recommendation}</p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-[#5B616E] mb-2 font-medium">By hour (UTC)</p>
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={optTime.by_hour}>
                          <XAxis dataKey="hour" tick={{ fontSize: 9 }} tickFormatter={h => `${h}h`} />
                          <YAxis tick={{ fontSize: 9 }} />
                          <Tooltip />
                          <Bar dataKey="views" fill="#B89B5E" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div>
                      <p className="text-xs text-[#5B616E] mb-2 font-medium">By day of week</p>
                      <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={optTime.by_day_of_week}>
                          <XAxis dataKey="day" tick={{ fontSize: 9 }} />
                          <YAxis tick={{ fontSize: 9 }} />
                          <Tooltip />
                          <Bar dataKey="views" fill="#1E5A8A" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              ) : <p className="text-sm text-[#5B616E] text-center py-8">Not enough data yet to determine optimal times</p>}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
